import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { setSentryUser, clearSentryUser } from '../utils/sentry';

// User interface
export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  roles: string[];
  permissions: string[];
  phone?: string;
  avatar_url?: string | null;
  organization_name?: string;
  status?: string;
}

// Auth state interface
interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

// Auth actions interface
interface AuthActions {
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  login: (user: User) => void;
  logout: () => void;
  clearError: () => void;
  updateUserProfile: (updates: Partial<User>) => void;
  updateUserAvatar: (avatarUrl: string | null) => void;
}

// Combined auth store type
export type AuthStore = AuthState & AuthActions;

// Create auth store
export const useAuthStore = create<AuthStore>()(
  persist(
    (set, _get) => ({
      // Initial state
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // Actions
      setUser: (user) =>
        set({
          user,
          isAuthenticated: !!user,
          error: null,
        }),

      setLoading: (isLoading) => set({ isLoading }),

      setError: (error) => set({ error }),

      login: (user) => {
        // Set Sentry user context for error tracking
        setSentryUser({
          id: user.id,
          email: user.email,
          username: user.full_name,
        });

        set({
          user,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
      },

      logout: () => {
        // Clear Sentry user context
        clearSentryUser();

        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
      },

      clearError: () => set({ error: null }),

      updateUserProfile: (updates) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : null,
        })),

      updateUserAvatar: (avatarUrl) =>
        set((state) => ({
          user: state.user ? { ...state.user, avatar_url: avatarUrl } : null,
        })),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// Selectors for better performance
export const useUser = () => useAuthStore((state) => state.user);
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
export const useAuthLoading = () => useAuthStore((state) => state.isLoading);
export const useAuthError = () => useAuthStore((state) => state.error);

// ─── Voting identity ──────────────────────────────────────────────
//
// The webapp's like/dislike endpoints attribute votes to a specific
// user by sending `{ user_id, email, name }` in the request body.
// This selector assembles a fully-validated snapshot of those three
// fields, or returns null if the store is missing any of them —
// typically because the current session predates a login coercion
// fix, or the user hasn't signed in yet.
//
// The `name.includes('undefined')` guard catches historical
// "undefined undefined" garbage that older sessions stored when a
// login mapper didn't default first/last to empty strings.

import logger from '@/utils/logger';

export interface VotingUserSnapshot {
  id: string;
  email: string;
  name: string;
}

export const useVotingUser = (): VotingUserSnapshot | null => {
  const u = useAuthStore((state) => state.user);
  if (!u?.id || !u.email) {
    return null;
  }
  const firstLast = `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim();
  const name = (u.full_name && u.full_name.trim()) || firstLast || u.email;
  if (!name || name.includes('undefined')) {
    logger.warn('[vote] Refusing — missing identity fields', {
      hasId: !!u.id,
      hasEmail: !!u.email,
      hasFullName: !!u.full_name,
      hasFirstName: !!u.first_name,
      hasLastName: !!u.last_name,
      fullNameLooksBroken: u.full_name?.includes?.('undefined') ?? false,
    });
    return null;
  }
  return { id: u.id, email: u.email, name };
};
